import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User, AuthResponse } from '../types/api';
import { apiService } from '../services/api';

interface AuthState {
  user: User | null;
  manager: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  } | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  subscriptionTier: 'free' | 'max';
}

type AuthAction =
  | { type: 'LOGIN_SUCCESS'; payload: AuthResponse }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'LOGOUT' }
  | { type: 'LOGOUT_USER_ONLY' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SUBSCRIPTION_TIER'; payload: 'free' | 'max' };

const initialState: AuthState = {
  user: null,
  manager: null,
  isAuthenticated: false,
  isLoading: true,
  subscriptionTier: 'free',
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        manager: (action.payload as any).manager || null,
        isAuthenticated: !!action.payload.user,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        manager: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'LOGOUT_USER_ONLY':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        // Keep manager session
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_SUBSCRIPTION_TIER':
      return {
        ...state,
        subscriptionTier: action.payload,
      };
    default:
      return state;
  }
}

interface AuthContextType extends AuthState {
  login: (phoneNumber: string, code: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: (clearManagerSession?: boolean) => void;
  setSubscriptionTier: (tier: 'free' | 'max') => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const token = sessionStorage.getItem('accessToken');
    const userData = sessionStorage.getItem('user');
    const managerSession = localStorage.getItem('managerSession');
    const hasManagerToken = !!localStorage.getItem('managerAccessToken');
    const hasManagerRefresh = !!localStorage.getItem('managerRefreshToken');
    const managerTokensValid = hasManagerToken || hasManagerRefresh;
    const savedTier = localStorage.getItem('subscriptionTier') as 'free' | 'max' | null;

    if (managerSession && !managerTokensValid) {
      localStorage.removeItem('managerSession');
    }

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        const manager = managerSession && managerTokensValid ? JSON.parse(managerSession) : null;
        dispatch({ type: 'LOGIN_SUCCESS', payload: { accessToken: token, refreshToken: '', user, manager } });
      } catch {
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('user');
      }
    } else if (managerSession && managerTokensValid) {
      try {
        const manager = JSON.parse(managerSession);
        dispatch({ type: 'LOGIN_SUCCESS', payload: { accessToken: '', refreshToken: '', user: null as any, manager } });
      } catch {
        localStorage.removeItem('managerSession');
      }
    }

    if (savedTier) {
      dispatch({ type: 'SET_SUBSCRIPTION_TIER', payload: savedTier });
    }

    dispatch({ type: 'SET_LOADING', payload: false });
  }, []);

  useEffect(() => {
    const handleManagerSessionExpired = () => {
      const hasManagerToken = !!localStorage.getItem('managerAccessToken');
      const hasManagerRefresh = !!localStorage.getItem('managerRefreshToken');
      if (!hasManagerToken && !hasManagerRefresh) {
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        apiService.logoutUser();
        dispatch({ type: 'LOGOUT' });
      }
    };

    window.addEventListener('managerSessionChange', handleManagerSessionExpired);
    return () => {
      window.removeEventListener('managerSessionChange', handleManagerSessionExpired);
    };
  }, []);

  const login = async (phoneNumber: string, code: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await apiService.login({ phoneNumber, code });
      sessionStorage.setItem('user', JSON.stringify(response.user));
      // Store refresh token for automatic token refresh (already done in apiService.login)
      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const register = async (data: any) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await apiService.register(data);
      sessionStorage.setItem('user', JSON.stringify(response.user));
      // Store refresh token for automatic token refresh (already done in apiService.register)
      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const logout = (clearManagerSession: boolean = false) => {
    sessionStorage.removeItem('user');
    localStorage.removeItem('subscriptionTier');
    sessionStorage.removeItem('refreshToken');

    if (clearManagerSession) {
      apiService.logout();
      dispatch({ type: 'LOGOUT' });
    } else {
      apiService.logoutUser();
      dispatch({ type: 'LOGOUT_USER_ONLY' });
    }
  };

  const setSubscriptionTier = (tier: 'free' | 'max') => {
    localStorage.setItem('subscriptionTier', tier);
    dispatch({ type: 'SET_SUBSCRIPTION_TIER', payload: tier });
  };

  const refreshUser = async () => {
    try {
      const user = await apiService.getCurrentUser();
      sessionStorage.setItem('user', JSON.stringify(user));
      dispatch({ type: 'UPDATE_USER', payload: user });
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        setSubscriptionTier,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
